import { Component, ChangeDetectionStrategy, inject, computed, signal } from '@angular/core';
import { Router, NavigationEnd, ActivatedRouteSnapshot } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map, startWith, finalize } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { TooltipModule } from 'primeng/tooltip';

import { environment } from '@environments/environment';
import { AppFacade } from '@core/services/facades/app.facade';
import { GrandCompteService } from '@core/services/grand-compte.service';
import { GrandCompteDnsService } from '@core/services/grand-compte-dns.service';
import { GrandCompteDnsApiService } from '@core/services/api/grand-compte-dns-api.service';
import { NotificationService } from '@core/services/notification.service';
import { DebugPanelService, DebugTab } from '@shared/services/debug-panel.service';
import { JsonTreeComponent } from '@shared/components/json-tree/json-tree.component';

const DEBUG_PANEL_ALLOWED_USER_IDS: readonly number[] = [567, 323];

function collectRouteData(snapshot: ActivatedRouteSnapshot): {
    params: Record<string, string>;
    queryParams: Record<string, string>;
    data: Record<string, unknown>;
} {
    let params: Record<string, string> = {};
    let data: Record<string, unknown> = {};
    let node: ActivatedRouteSnapshot | null = snapshot;

    while (node) {
        params = { ...params, ...node.params };
        data = { ...data, ...node.data };
        node = node.firstChild;
    }

    return { params, queryParams: snapshot.queryParams, data };
}

/** Route/query params are often just btoa()'d (e.g. campaign.model.ts getNavigationQueryParams), not encrypted — decode them for readability. */
function tryDecodeBase64(value: string): string | null {
    if (value.length === 0 || value.length % 4 !== 0 || !/^[A-Za-z0-9+/]+={0,2}$/.test(value)) return null;
    try {
        const decoded = atob(value);
        return /^[\x20-\x7E]+$/.test(decoded) ? decoded : null;
    } catch {
        return null;
    }
}

function decodeBase64Entries(entries: Record<string, string>): Record<string, string> {
    const decoded: Record<string, string> = {};
    for (const [key, value] of Object.entries(entries)) {
        const result = tryDecodeBase64(value);
        if (result !== null) decoded[key] = result;
    }
    return decoded;
}

@Component({
    selector: 'app-debug-panel',
    imports: [JsonTreeComponent, TooltipModule],
    templateUrl: './debug-panel.component.html',
    styleUrls: ['./debug-panel.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DebugPanelComponent {
    private readonly appFacade = inject(AppFacade);
    private readonly grandCompteService = inject(GrandCompteService);
    private readonly grandCompteDnsService = inject(GrandCompteDnsService);
    private readonly grandCompteDnsApi = inject(GrandCompteDnsApiService);
    private readonly router = inject(Router);
    private readonly store = inject(Store);
    private readonly notificationService = inject(NotificationService);

    protected readonly panel = inject(DebugPanelService);
    protected readonly resyncing = signal(false);

    readonly visible =
        this.appFacade.isAuthenticated && (!environment.production || DEBUG_PANEL_ALLOWED_USER_IDS.includes(this.appFacade.currentUser?.id ?? -1));

    private readonly routeSnapshot = toSignal(
        this.router.events.pipe(
            filter((event) => event instanceof NavigationEnd),
            map(() => this.router.routerState.snapshot.root),
            startWith(this.router.routerState.snapshot.root),
        ),
        { initialValue: this.router.routerState.snapshot.root },
    );

    private readonly storeSnapshot = toSignal(this.store as unknown as Observable<unknown>, { initialValue: {} });

    constructor() {
        this.panel.registerBuiltInTab({
            id: 'session',
            label: 'Session',
            getData: () => ({
                appVersion: environment.appVersion,
                sentryRelease: environment.sentryRelease,
                user: {
                    id: this.appFacade.currentUser?.id,
                    email: this.appFacade.currentUser?.email,
                    role: this.appFacade.currentUser?.role,
                    isAdmin: this.appFacade.isAdmin,
                },
                grandCompte: {
                    id: this.grandCompteService.currentGrandCompteId(),
                    is_agency: this.grandCompteService.currentGc()?.is_agency,
                },
                tenant: this.grandCompteDnsService.currentGcByDns(),
                currency: this.appFacade.selectedCurrency,
            }),
        });

        this.panel.registerBuiltInTab({
            id: 'route',
            label: 'Route',
            getData: () => {
                const { params, queryParams, data } = collectRouteData(this.routeSnapshot());
                return {
                    url: this.router.url,
                    params,
                    queryParams,
                    data,
                    decoded: { ...decodeBase64Entries(params), ...decodeBase64Entries(queryParams) },
                };
            },
        });

        this.panel.registerBuiltInTab({
            id: 'store',
            label: 'Store',
            getData: () => this.storeSnapshot(),
        });
    }

    protected activeTabData = computed(() => {
        const activeId = this.panel.activeTabId();
        const tab = this.panel.tabs().find((t: DebugTab) => t.id === activeId);
        return tab?.getData() ?? null;
    });

    protected togglePanel(): void {
        this.panel.open.update((open) => !open);
    }

    protected selectTab(tabId: string): void {
        this.panel.activeTabId.set(tabId);
    }

    protected copyToClipboard(): void {
        this.panel.copyActiveTabToClipboard();
    }

    protected resyncGcDesign(): void {
        this.resyncing.set(true);
        this.grandCompteDnsApi
            .syncGcDesign()
            .pipe(finalize(() => this.resyncing.set(false)))
            .subscribe({
                next: () => {
                    this.notificationService.showToast(
                        { subject: 'Design resynced', message: '/sync-gc-design succeeded', duration: 4000, isExpandable: false, icon: '✅' },
                        'success',
                    );
                },
                error: (error: unknown) => {
                    console.error('[debug-panel] /sync-gc-design failed', error);
                    const detail = error instanceof HttpErrorResponse ? `${error.status} ${error.statusText}` : 'Unknown error';
                    this.notificationService.showToast(
                        { subject: 'Resync failed', message: detail, duration: 8000, isExpandable: false, icon: '❌' },
                        'error',
                    );
                },
            });
    }
}
